import { supabase } from '../config/supabase';
import { User } from '../types';

/**
 * AuthUserService - Gestiona usuarios usando Funciones RPC de Supabase (Postgres Functions)
 * Requiere ejecutar el script FUNCIONES-ADMIN.sql en Supabase primero.
 */
export class AuthUserService {
    /**
     * Crea un usuario llamando a la función RPC admin_create_user
     */
    static async createUser(userData: Omit<User, 'id'> & { password: string }): Promise<{ success: boolean; user?: User; error?: string }> {
        try {
            const { data, error } = await supabase.rpc('admin_create_user', {
                new_email: userData.email || `${userData.name.replace(/\s/g, '').toLowerCase()}@temp.local`,
                new_password: userData.password,
                new_name: userData.name,
                new_role: userData.role,
                new_site_id: userData.siteId || null,
                new_avatar: userData.avatar || userData.name.substring(0, 2).toUpperCase()
            });

            if (error) throw error;

            if (data && data.success) {
                return { success: true };
            } else {
                throw new Error('La función no retornó éxito');
            }
        } catch (error: any) {
            console.error('Error creating user via RPC:', error);
            return {
                success: false,
                error: error.message || 'Error al crear usuario'
            };
        }
    }

    /**
     * Actualiza usuario y/o contraseña llamando a RPC admin_update_user
     */
    static async updateUser(userId: string, updates: Partial<User> & { password?: string }): Promise<{ success: boolean; error?: string }> {
        try {
            const { data, error } = await supabase.rpc('admin_update_user', {
                target_user_id: userId,
                new_password: updates.password || null, // null si no se cambia
                new_name: updates.name || null,
                new_role: updates.role || null,
                new_site_id: updates.siteId || null
            });

            if (error) {
                console.error('❌ RPC Error:', error);
                throw error;
            }

            return { success: true };
        } catch (error: any) {
            console.error('❌ Error updating user via RPC:', error);
            return {
                success: false,
                error: error.message || 'Error al actualizar usuario'
            };
        }
    }

    /**
     * Elimina usuario llamando a RPC admin_delete_user
     */
    static async deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
        try {
            const { data, error } = await supabase.rpc('admin_delete_user', {
                target_user_id: userId
            });

            if (error) throw error;

            return { success: true };
        } catch (error: any) {
            console.error('Error deleting user via RPC:', error);
            return {
                success: false,
                error: error.message || 'Error al eliminar usuario'
            };
        }
    }
}
